import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons/faChevronRight';
import { TypedMemo } from '../../common/types';
import { CreationSlugField } from './CreationSlugField';
import { CreationThemeRow } from './CreationThemeRow';
import { CreationTemplateId, creationTemplateIds } from './blankTemplates';
import { CreationDraft } from './props';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the sheet; each part uses its own slice of it */
import styles from './SMLCreationSheet.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CreationBlankStageProps {
  name: string;
  onNameChange: (name: string) => void;
  template: CreationTemplateId;
  onTemplateChange: (template: CreationTemplateId) => void;
  draft: CreationDraft;
  slugPrefix: string;
}

interface TemplateRowProps {
  id: CreationTemplateId;
  isOn: boolean;
  title: string;
  blocks: string;
  onSelect: (id: CreationTemplateId) => void;
}

const TemplateRow: React.FC<TemplateRowProps> = ({ id, isOn, title, blocks, onSelect }) => {
  const select = useCallback(() => onSelect(id), [id, onSelect]);
  return (
    <button type="button" className={clsx(styles.row, isOn && styles.rowSelected)} onClick={select}>
      <span className={styles.rowInfo}>
        <span className={styles.rowName}>{title}</span>
        <span className={styles.rowSub}>{blocks}</span>
      </span>
      <FontAwesomeIcon icon={faChevronRight} className={styles.rowGo} />
    </button>
  );
};

/**
 * A page about something that is not a release.
 *
 * Each starting point names the blocks it actually lays down. The design drew
 * dates, tickets, a product and a countdown; none of those blocks exist, and a
 * template that named them would leave the artist looking for rows that never
 * arrive.
 */
const CreationBlankStageComponent: React.FC<CreationBlankStageProps> = ({
  name,
  onNameChange,
  template,
  onTemplateChange,
  draft,
  slugPrefix,
}) => {
  const intl = useIntl();

  const onName = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => onNameChange(event.target.value),
    [onNameChange]
  );

  const copy = useMemo(
    () => ({
      [CreationTemplateId.empty]: {
        title: intl.formatMessage({ id: 'app.sml.create.template.empty', defaultMessage: 'Empty page' }),
        blocks: intl.formatMessage({
          id: 'app.sml.create.template.empty.blocks',
          defaultMessage: 'Profile block only. Add the rest yourself.',
        }),
      },
      [CreationTemplateId.linkHub]: {
        title: intl.formatMessage({ id: 'app.sml.create.template.link_hub', defaultMessage: 'Link hub' }),
        blocks: intl.formatMessage({
          id: 'app.sml.create.template.link_hub.blocks',
          defaultMessage: 'Profile · socials. Add a link block per destination.',
        }),
      },
      [CreationTemplateId.tour]: {
        title: intl.formatMessage({ id: 'app.sml.create.template.tour', defaultMessage: 'Tour' }),
        blocks: intl.formatMessage({
          id: 'app.sml.create.template.tour.blocks',
          defaultMessage: 'Profile · a “Tour dates” heading · socials.',
        }),
      },
      [CreationTemplateId.merch]: {
        title: intl.formatMessage({ id: 'app.sml.create.template.merch', defaultMessage: 'Merch drop' }),
        blocks: intl.formatMessage({
          id: 'app.sml.create.template.merch.blocks',
          defaultMessage: 'Profile · a “The drop” heading · socials.',
        }),
      },
    }),
    [intl]
  );

  const nameLabel = intl.formatMessage({
    id: 'app.sml.create.blank.name',
    defaultMessage: 'What is this page for?',
  });

  return (
    <>
      <div className={styles.fields}>
        <div className={clsx(styles.field, styles.fieldWide)}>
          <span className={styles.fieldLabel}>{nameLabel}</span>
          <div className={styles.fieldBox}>
            <input
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              className={styles.fieldInput}
              value={name}
              aria-label={nameLabel}
              placeholder={intl.formatMessage({
                id: 'app.sml.create.blank.name.placeholder',
                defaultMessage: 'Tour dates, merch drop, link hub…',
              })}
              onChange={onName}
            />
          </div>
        </div>
        <CreationSlugField
          slug={draft.slug}
          prefix={slugPrefix}
          platformDomain={draft.platformDomain}
          onPlatformDomainChange={draft.setPlatformDomain}
        />
      </div>

      <div>
        <div className={styles.hint}>
          <span className={styles.hintLabel}>
            {intl.formatMessage({ id: 'app.sml.create.template.label', defaultMessage: 'Start from' })}
          </span>
          <span className={styles.hintValue}>
            {intl.formatMessage({ id: 'app.sml.create.template.hint', defaultMessage: 'blocks, all editable' })}
          </span>
        </div>
        <div className={styles.rows}>
          {creationTemplateIds.map((id) => (
            <TemplateRow
              key={id}
              id={id}
              isOn={template === id}
              title={copy[id].title}
              blocks={copy[id].blocks}
              onSelect={onTemplateChange}
            />
          ))}
        </div>
      </div>

      <CreationThemeRow theme={draft.theme} onChange={draft.setTheme} />
    </>
  );
};

export const CreationBlankStage = TypedMemo(CreationBlankStageComponent);
